import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Theme,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { makeStyles } from 'tss-react/mui';
import { AxiosError } from 'axios';

import useSnackBar from '../../../hooks/useSnackBar';
import { updateSettlementAmount } from '../../../api/paymentsApi/settlementsApi';
import { ErrorData } from '../../../types';
import FormTextField from '../../../components/formFields/FormTextField';
import FormCancelButton from '../../../components/buttons/FormCancelButton';
import FormSubmitButton from '../../../components/buttons/FormSubmitButton';
import FormCheckboxField from '../../../components/formFields/FormCheckboxField';

// Dialog, title and button styles mirror the shared Popup component.
const useStyles = makeStyles()((theme: Theme) => ({
  dialogContainer: {
    '& .MuiDialog-paper': {
      width: '100%',
      maxWidth: '560px',
      boxSizing: 'border-box',
      padding: '32px',
      gap: '32px',
      border: `1px solid ${theme.palette.secondary.dark}`,
      borderRadius: '16px',
      backgroundColor: theme.palette.primary.light,
    },
  },
  dialogTitle: {
    fontFamily: 'Poppins',
    fontWeight: '600',
    fontSize: '34px',
    lineHeight: '40.8px',
    color: theme.palette.common.white,
    padding: '32px 0px 0px 0px',
  },
  subtitle: {
    fontFamily: 'Poppins',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '16.8px',
    color: theme.palette.text.disabled,
    marginTop: '12px',
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    padding: '8px 0px 0px 0px',
    overflowY: 'unset',
  },
  fieldWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    fontFamily:'Poppins',
    fontSize:'16px'
  },
  errorText: {
    fontFamily: 'Poppins',
    fontSize: '13px',
    color: theme.palette.error.main,
    marginLeft: '14px',
  },
  // Same layout as the Cancel/Save row in SettlementAttorneyFeePopup.
  dialogActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0px',

    button: {
      flexBasis: '115px',
    },
  },
}));

interface EditSettlementAmountsFormValues {
  settlementAmount: string;
  pipAmount: string;
  lienReimbursementAmount: string;
  understood: boolean;
}

const toFormAmount = (value?: number) => (value !== undefined && value !== null ? String(value) : '');

// Blank, non-numeric or negative input is invalid.
const isInvalidAmount = (value: string) =>
  value.trim() === '' || Number.isNaN(Number(value)) || Number(value) < 0;

interface EditSettlementAmountsPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  settlementId: number;
  showPipAmount: boolean;
  showLienReimbursementAmount?: boolean;
  initialSettlementAmount?: number;
  initialPipAmount?: number;
  initialLienReimbursementAmount?: number;
}

const EditSettlementAmountsPopup = ({
  isOpen,
  onClose,
  onSuccess,
  settlementId,
  showPipAmount,
  showLienReimbursementAmount = false,
  initialSettlementAmount,
  initialPipAmount,
  initialLienReimbursementAmount,
}: EditSettlementAmountsPopupProps) => {
  const { classes } = useStyles();
  const { setAlert } = useSnackBar();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formMethods = useForm<EditSettlementAmountsFormValues>({
    defaultValues: { settlementAmount: '', pipAmount: '', lienReimbursementAmount: '', understood: false },
  });
  const { reset, watch } = formMethods;

  const settlementAmount = watch('settlementAmount');
  const pipAmount = watch('pipAmount');
  const lienReimbursementAmount = watch('lienReimbursementAmount');
  const understood = watch('understood');

  // With PIP (standard_pip) or lien reimbursement (lien_repayment), either amount may be zero, but not both.
  const hasSecondaryAmount = showPipAmount || showLienReimbursementAmount;
  const secondaryAmount = showPipAmount ? pipAmount : lienReimbursementAmount;
  const isSettlementAmountInvalid = hasSecondaryAmount
    ? isInvalidAmount(settlementAmount)
    : isInvalidAmount(settlementAmount) || Number(settlementAmount) <= 0;
  const isPipAmountInvalid = showPipAmount && isInvalidAmount(pipAmount);
  const isLienReimbursementAmountInvalid =
    showLienReimbursementAmount && isInvalidAmount(lienReimbursementAmount);
  const isTotalZero =
    hasSecondaryAmount &&
    !isSettlementAmountInvalid &&
    !isPipAmountInvalid &&
    !isLienReimbursementAmountInvalid &&
    Number(settlementAmount) + Number(secondaryAmount) <= 0;

  useEffect(() => {
    if (isOpen) {
      reset({
        settlementAmount: toFormAmount(initialSettlementAmount),
        pipAmount: toFormAmount(initialPipAmount),
        lienReimbursementAmount: toFormAmount(initialLienReimbursementAmount),
        understood: false,
      });
    }
  }, [isOpen, initialSettlementAmount, initialPipAmount, initialLienReimbursementAmount]);

  const handleApply = async () => {
    setIsSubmitting(true);
    try {
      await updateSettlementAmount(settlementId, {
        settledAmount: Number(settlementAmount) || 0,
        pipAmount: showPipAmount ? Number(pipAmount) || 0 : 0,
        ...(showLienReimbursementAmount && {
          lienReimbursementAmount: Number(lienReimbursementAmount) || 0,
        }),
      });

      setAlert((prev) => ({
        ...prev,
        message: 'Amounts updated successfully.',
        type: 'success',
      }));

      onSuccess();
      onClose();
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      setAlert((prev) => ({
        ...prev,
        message: axiosError?.response?.data?.message || 'Failed to update amounts.',
        type: 'error',
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog className={classes.dialogContainer} open={isOpen} onClose={onClose}>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={(theme) => ({
          position: 'absolute',
          top: 32,
          right: 32,
          color: theme.palette.common.white,
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogTitle className={classes.dialogTitle}>
        Edit Amounts
        <Typography className={classes.subtitle}>
          Changing an amount recalculates the attorney fees and the net settlement on this settlement.
        </Typography>
      </DialogTitle>
      <FormProvider {...formMethods}>
        <DialogContent className={classes.dialogContent}>
          <Box className={classes.fieldWrapper}>
            <FormTextField
              name="settlementAmount"
              label="Settlement Amount *"
              valueDefinition="$"
              inputProps={{ inputMode: 'decimal', style: { fontFamily: 'Poppins', fontSize: '16px' } }}
              inputLabelProps={{ sx: { fontFamily: 'Poppins', fontSize: '16px' } }}
            />
            {isSettlementAmountInvalid && (
              <Typography className={classes.errorText}>
                {hasSecondaryAmount
                  ? 'Enter a settlement amount of zero or more.'
                  : 'Enter a settlement amount greater than zero.'}
              </Typography>
            )}
          </Box>

          {showPipAmount && (
            <Box className={classes.fieldWrapper}>
              <FormTextField
                name="pipAmount"
                label="PIP Amount *"
                valueDefinition="$"
                inputProps={{ inputMode: 'decimal' }}
              />
              {isPipAmountInvalid && (
                <Typography className={classes.errorText}>
                  Enter a PIP amount of zero or more.
                </Typography>
              )}
              {isTotalZero && (
                <Typography className={classes.errorText}>
                  Settlement amount and PIP amount cannot both be zero.
                </Typography>
              )}
            </Box>
          )}

          {showLienReimbursementAmount && (
            <Box className={classes.fieldWrapper}>
              <FormTextField
                name="lienReimbursementAmount"
                label="Lien Reimbursement Amount *"
                valueDefinition="$"
                inputProps={{ inputMode: 'decimal' }}
              />
              {isLienReimbursementAmountInvalid && (
                <Typography className={classes.errorText}>
                  Enter a lien reimbursement amount of zero or more.
                </Typography>
              )}
              {isTotalZero && (
                <Typography className={classes.errorText}>
                  Settlement amount and lien reimbursement amount cannot both be zero.
                </Typography>
              )}
            </Box>
          )}

          <FormCheckboxField
            name="understood"
            label="I understand this recalculates fees and the net settlement"
          />
        </DialogContent>
      </FormProvider>
      <DialogActions className={classes.dialogActions} disableSpacing>
        <FormCancelButton onClick={onClose} disabled={isSubmitting} />
        <FormSubmitButton
          buttonText="Apply"
          onClick={handleApply}
          disabled={!understood || isSubmitting || isSettlementAmountInvalid || isPipAmountInvalid || isLienReimbursementAmountInvalid || isTotalZero}
        />
      </DialogActions>
    </Dialog>
  );
};

export default EditSettlementAmountsPopup;
